import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Globe, Phone, Mail, Search, X, Bookmark, MousePointerClick, CheckCircle2, Send, BellOff } from 'lucide-react';
import { Context } from 'Context/Context';
import { getAllCasesApi, searchWithCaseNumberApi, outreachFirmsApi, outreachCaseFirmsApi, outreachFirmEmailApi, outreachSendApi } from 'Constants';
import Header from 'Components/Header/Header';
import useIsMobile from 'Hooks/useIsMobile';
import CaseCard from 'Components/Professional/Cases/CaseCard';
import { CardStack, CardSkeleton, CaseBtn, CasePager, EmptyState } from 'Components/Professional/Cases/CaseListUI';
import QcrLoader from 'Components/Icons/QcrLoader';
import OutreachEmailModal from './OutreachEmailModal';
import { firmsOfCase, firmKey, isEmail, withScheme } from './outreachTemplates';

// Admin > Firm outreach. Firms our AI recommends to clients (and which ones
// clients saved or clicked); add a firm's email and send it an invite to claim
// its free profile. Data + sending: server/routes/outreach.js.

const CASES_PAGE = 10;
const FIRMS_PAGE = 15;
const ADMIN_VIEWER = { isActiveSubscription: true, role: 0 }; // admins see every case detail, nothing blurred
const statsOf = (d) => ({ [d?._id]: { requested: Math.max(0, (d?.requests || 0) - (d?.review || 0)), approved: d?.review || 0 } });
const apiMessage = (err, fallback) => err?.response?.data?.message || fallback;

function Badge({ tone = 'navy', icon: Icon, children, title }) {
  const tones = {
    navy: 'text-navy/[.7] bg-navy/[.05] border-navy/[.14]',
    brass: 'text-brass bg-brass/[.08] border-brass/[.3]',
    green: 'text-success bg-success/[.08] border-success/[.3]',
    red: 'text-danger bg-danger/[.07] border-danger/[.3]',
  };
  return (
    <span title={title} className={`inline-flex items-center gap-1 font-semibold text-[11px] leading-none border rounded-[20px] py-[5px] px-2 whitespace-nowrap ${tones[tone]}`}>
      {Icon && <Icon className='w-3 h-3' strokeWidth={2.2} />}{children}
    </span>
  );
}

// When can this firm be emailed again (null = now)
const nextAllowedAt = (status, cooldownDays) => {
  const last = status?.outreach?.lastSentAt;
  if (!last) return null;
  const next = moment(last).add(cooldownDays, 'days');
  return next.isAfter(moment()) ? next : null;
};

// One firm: who it is, how clients reacted, its status, the email box and the button.
function FirmRow({ firm, status, email, onEmail, onEmailBlur, onCompose, cooldownDays, extra }) {
  const ok = isEmail(email);
  const optedOut = !!status?.optedOut;
  const waitUntil = nextAllowedAt(status, cooldownDays);
  const noRecord = !firm.firmId;
  const blocked = noRecord || optedOut || !!waitUntil;
  const why = noRecord ? 'This firm has no record in our database yet'
    : optedOut ? 'This firm unsubscribed from our emails'
    : waitUntil ? `Emailed recently - next email possible ${waitUntil.format('MMM D')}`
    : ok ? 'Write the email' : "Add the firm's email first";
  return (
    <div className='flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-5 py-3.5 border-b border-navy/[.08] last:border-b-0 font-plexsans'>
      <div className='flex-1 min-w-0 flex flex-col gap-1.5'>
        <div className='flex flex-wrap items-center gap-2'>
          <span className='font-semibold text-[14px] leading-[1.3] text-navy [overflow-wrap:anywhere]'>{firm.name}</span>
          {firm.group && <Badge>{firm.group}</Badge>}
          {firm.savedByClient && <Badge tone='brass' icon={Bookmark}>{firm.savedCount > 1 ? `Saved by ${firm.savedCount} clients` : 'Saved by client'}</Badge>}
          {firm.clickedByClient && <Badge icon={MousePointerClick}>{firm.clickedCount > 1 ? `Website clicked ×${firm.clickedCount}` : 'Website clicked'}</Badge>}
          {status?.registered === 'verified' && <Badge tone='green' icon={CheckCircle2}>On Quick Case Review</Badge>}
          {status?.registered === 'signedUp' && <Badge tone='green'>Signed up · not verified</Badge>}
          {optedOut && <Badge tone='red' icon={BellOff}>Unsubscribed</Badge>}
          {status?.outreach?.lastSentAt && <Badge icon={Send} title={`${status.outreach.sentCount} email(s) sent`}>Emailed {moment(status.outreach.lastSentAt).format('MMM D')}</Badge>}
          {status?.outreach?.lastStatus === 'failed' && <Badge tone='red'>Last email failed</Badge>}
        </div>
        <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] leading-[1.4] text-navy/[.6]'>
          {firm.website && <a href={withScheme(firm.website)} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1 text-brass underline hover:text-brassDark [overflow-wrap:anywhere]'><Globe className='w-3.5 h-3.5 flex-none' />{String(firm.website).replace(/^https?:\/\//, '').replace(/\/$/, '')}</a>}
          {firm.phone && <span className='inline-flex items-center gap-1'><Phone className='w-3.5 h-3.5' />{firm.phone}</span>}
          {firm.fitRate != null && firm.fitRate !== '' && !isNaN(Number(firm.fitRate)) && <span>Fit {Number(firm.fitRate).toFixed(1)}/5</span>}
        </div>
        {extra}
      </div>
      <div className='flex flex-col sm:flex-row gap-2 lg:w-[400px] flex-none'>
        <label className='flex-1 relative'>
          <Mail className='w-4 h-4 text-navy/[.35] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none' />
          <input type='email' value={email || ''} onChange={(e) => onEmail(e.target.value)} onBlur={onEmailBlur}
            placeholder="Firm's email" disabled={noRecord || optedOut}
            className={`w-full h-10 pl-9 pr-3 text-[13.5px] text-navy bg-white border rounded-md focus:outline-none focus:border-brass disabled:bg-navy/[.03] ${email && !ok ? 'border-danger/[.6]' : 'border-navy/[.2]'}`} />
        </label>
        <CaseBtn onClick={onCompose} className='!py-2.5 !px-4 h-10' title={why} disabled={blocked || !ok}>Email firm</CaseBtn>
      </div>
    </div>
  );
}

function FirmOutreach() {
  const { makeApiCallFunc, setSelectedMenuItem } = useContext(Context);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('firms');

  // shared across both tabs, keyed by firm id
  const [statusByFirm, setStatusByFirm] = useState({});   // { email, optedOut, registered, account, outreach }
  const [emails, setEmails] = useState({});                // what the admin typed
  const [cooldownDays, setCooldownDays] = useState(0);   // server sends the real value (0 = no wait)
  const [sendingReady, setSendingReady] = useState(false);
  const [composer, setComposer] = useState(null);          // { firm, cases }

  // By firm (server-paginated)
  const [firmsPage, setFirmsPage] = useState(null);        // { firms, total }
  const [firmOffset, setFirmOffset] = useState(0);
  const [firmSearch, setFirmSearch] = useState('');
  const [firmQuery, setFirmQuery] = useState('');          // debounced

  // By case
  const [casesOpened, setCasesOpened] = useState(false);
  const [caseOffset, setCaseOffset] = useState(0);
  const [cases, setCases] = useState(null);
  const [casesTotal, setCasesTotal] = useState(0);
  const [links, setLinks] = useState({});                  // "caseId:firmId" -> { saved, clicked }
  const [caseQuery, setCaseQuery] = useState('');
  const [searchHit, setSearchHit] = useState(null);        // case | 'none' | null

  useEffect(() => { setSelectedMenuItem('4'); }, [setSelectedMenuItem]);

  const takeMeta = (data) => {
    if (typeof data?.cooldownDays === 'number') setCooldownDays(data.cooldownDays);
    setSendingReady(!!data?.sendingReady);
  };
  const mergeStatus = (map) => setStatusByFirm((prev) => ({ ...prev, ...(map || {}) }));

  // ---- By firm ----
  useEffect(() => {
    const t = setTimeout(() => { setFirmQuery(firmSearch.trim()); setFirmOffset(0); }, 300);
    return () => clearTimeout(t);
  }, [firmSearch]);

  useEffect(() => {
    if (tab !== 'firms') return;
    let live = true;
    setFirmsPage(null);
    makeApiCallFunc(outreachFirmsApi, { skip: firmOffset, limit: FIRMS_PAGE, search: firmQuery },
      ({ data }) => {
        if (!live) return;
        takeMeta(data);
        const firms = data?.firms || [];
        mergeStatus(Object.fromEntries(firms.map((f) => [String(f.firmId), { email: f.email, optedOut: f.optedOut, registered: f.registered, account: f.account, outreach: f.outreach }])));
        setFirmsPage({ firms, total: data?.total || 0 });
      },
      (err) => { if (live) { toast.error(apiMessage(err, 'Could not load firms.')); setFirmsPage({ firms: [], total: 0 }); } },
      null, true);
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, firmOffset, firmQuery]);

  // ---- By case ----
  useEffect(() => { if (tab === 'cases') setCasesOpened(true); }, [tab]);

  const loadCaseFirms = useCallback((list) => {
    const ids = (list || []).map((c) => c._id).filter(Boolean);
    if (ids.length === 0) return;
    makeApiCallFunc(outreachCaseFirmsApi, { caseIds: ids },
      ({ data }) => {
        takeMeta(data);
        mergeStatus(data?.firms);
        setLinks((prev) => {
          const next = { ...prev };
          (data?.links || []).forEach((l) => { next[`${l.caseId}:${l.firmId}`] = { saved: !!l.saved, clicked: !!l.clicked }; });
          return next;
        });
      },
      (err) => console.log(err), null, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!casesOpened) return;
    let live = true;
    setCases(null);
    makeApiCallFunc(getAllCasesApi, { paginatelimit: { skip: caseOffset, limit: CASES_PAGE } },
      ({ data }) => {
        if (!live) return;
        const list = data?.cases || [];
        setCases(list); setCasesTotal(data?.noEntries || 0);
        loadCaseFirms(list);
      },
      (err) => { if (live) { console.log(err); setCases([]); } }, null, true);
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseOffset, casesOpened]);

  const runCaseSearch = (e) => {
    e && e.preventDefault();
    const q = caseQuery.trim();
    if (!q) { setSearchHit(null); return; }
    makeApiCallFunc(searchWithCaseNumberApi, { caseNumber: q },
      ({ data }) => {
        const hit = data && Object.keys(data).length ? data : 'none';
        setSearchHit(hit);
        if (hit !== 'none') loadCaseFirms([hit]);
      },
      () => setSearchHit('none'));
  };

  // ---- emails ----
  // a firm that already has an account is emailed at the account's address
  const emailOf = (firm) => emails[firm.key] ?? statusByFirm[firm.firmId]?.account?.email ?? statusByFirm[firm.firmId]?.email ?? firm.email ?? '';
  const setEmail = (key, v) => setEmails((prev) => ({ ...prev, [key]: v }));
  const saveEmail = (firm) => {
    if (!firm.firmId) return;
    const typed = emails[firm.key];
    if (typed === undefined) return;
    const clean = typed.trim().toLowerCase();
    if (clean === (statusByFirm[firm.firmId]?.email || '') || (clean && !isEmail(clean))) return;
    makeApiCallFunc(outreachFirmEmailApi, { firmId: firm.firmId, email: clean },
      ({ data }) => mergeStatus({ [firm.firmId]: { ...(statusByFirm[firm.firmId] || {}), email: data?.email || null } }),
      (err) => toast.error(apiMessage(err, 'Could not save the email.')), null, true);
  };

  const send = (payload) => new Promise((resolve, reject) => {
    const firm = composer?.firm;
    makeApiCallFunc(outreachSendApi, { firmId: firm.firmId, ...payload },
      ({ data }) => {
        mergeStatus({ [firm.firmId]: data?.status || {} });
        toast.success(`Email sent to ${firm.name}.`);
        setComposer(null);
        resolve();
      },
      (err) => {
        // keep the page in step with what the server found (opt-out, cooldown)
        const code = err?.response?.data?.code;
        if (code === 'OPTED_OUT') mergeStatus({ [firm.firmId]: { ...(statusByFirm[firm.firmId] || {}), optedOut: true } });
        reject(err);
      }, null, true);
  });

  // ---- view helpers ----
  const caseFirm = (d, f) => {
    const firmId = f.firmid ? String(f.firmid) : null;
    const link = firmId ? links[`${d._id}:${firmId}`] : null;
    return { ...f, firmId, key: firmId || firmKey(f), savedByClient: !!link?.saved, clickedByClient: !!link?.clicked };
  };

  const recommendedFirms = (d) => {
    const firms = firmsOfCase(d).map((f) => caseFirm(d, f));
    return (
      <div className='bg-cream/[.6] border-t border-navy/[.1] px-4 py-3 lg:px-[30px] lg:py-4 font-plexsans'>
        <div className='flex items-baseline justify-between gap-3'>
          <span className='font-plexmono font-medium text-[11px] leading-none tracking-[.08em] uppercase text-brass'>Recommended firms · {firms.length}</span>
          {firms.length > 0 && <span className='hidden sm:inline text-[12px] text-navy/[.5]'>From the AI firm match for this case</span>}
        </div>
        {firms.length === 0
          ? <p className='m-0 mt-2.5 text-[13px] text-navy/[.55]'>No firms were recommended for this case{d?.aiFirmMatchStatus === 3 ? ' - the firm match failed.' : '.'}</p>
          : <div className='mt-1 bg-white border border-navy/[.1] rounded-md px-3.5'>
              {firms.map((f) => (
                <FirmRow key={f.key + f.group} firm={f} status={statusByFirm[f.firmId]} cooldownDays={cooldownDays}
                  email={emailOf(f)} onEmail={(v) => setEmail(f.key, v)} onEmailBlur={() => saveEmail(f)}
                  onCompose={() => setComposer({ firm: f, cases: [d] })} />
              ))}
            </div>}
      </div>
    );
  };

  const caseCard = (d) => (
    <CaseCard key={d._id} detail={d} userInfo={ADMIN_VIEWER} reqRevCountArr={statsOf(d)}
      actions={<CaseBtn variant='secondary' onClick={() => navigate('/admin/caseDetails/' + d._id)} full={isMobile}>View case</CaseBtn>}
      expanded={recommendedFirms(d)} />
  );

  const tabBtn = (key, label, count) => (
    <button type='button' onClick={() => setTab(key)}
      className={`font-plexsans font-semibold text-[13.5px] leading-none bg-transparent border-0 border-b-2 pb-3 pt-1 px-0 cursor-pointer ${tab === key ? 'text-navy border-brass' : 'text-navy/[.5] border-transparent hover:text-navy'}`}>
      {label}{count != null && <span className='ml-1.5 font-plexmono text-[11px] text-navy/[.45]'>{count}</span>}
    </button>
  );

  const searchBox = (value, onChange, placeholder) => (
    <label className='relative flex-1 sm:max-w-[340px]'>
      <Search className='w-4 h-4 text-navy/[.35] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none' />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className='w-full h-10 pl-9 pr-3 text-[13.5px] text-navy bg-white border border-navy/[.2] rounded-md focus:outline-none focus:border-brass' />
    </label>
  );

  return (
    <>
      <Header />
      <div className='col-span-6 w-full font-plexsans bg-cream min-h-[calc(100vh-64px)]'>
        <div className='w-full max-w-[1180px] mx-auto px-4 py-6 lg:px-10 lg:py-10 flex flex-col gap-5'>
          <div className='flex flex-col gap-1.5'>
            <span className='font-plexmono font-medium text-[11px] leading-none tracking-[.08em] uppercase text-brass'>Admin</span>
            <h1 className='m-0 font-spectral font-normal text-[26px] lg:text-[30px] leading-[1.2] text-navy'>Firm outreach</h1>
            <p className='m-0 text-[13.5px] leading-[1.55] text-navy/[.6] max-w-[720px]'>
              Let firms know clients are finding them. See the firms our AI recommends, add a firm's email and send it a short note inviting it to claim its free profile.
            </p>
          </div>

          <div className='flex gap-7 border-b border-navy/[.12]'>
            {tabBtn('firms', 'By firm', firmsPage ? firmsPage.total : null)}
            {tabBtn('cases', 'By case', casesOpened && cases ? casesTotal : null)}
          </div>

          {tab === 'firms' && <>
            <div className='flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between'>
              {searchBox(firmSearch, setFirmSearch, 'Search firm name or website')}
              <span className='text-[12px] text-navy/[.5]'>Saved by clients first, then most recommended</span>
            </div>
            {firmsPage === null
              ? <div className='flex justify-center py-16'><QcrLoader variant='draw' size={56} label='Loading firms' /></div>
              : firmsPage.firms.length === 0
                ? <EmptyState title='No firms found' body={firmQuery ? 'No firm matches that search.' : 'Firms show here once cases have AI firm matches.'} />
                : <>
                    <div className='bg-white border border-navy/[.12] rounded-md px-4 lg:px-6'>
                      {firmsPage.firms.map((raw) => {
                        const f = { ...raw, key: String(raw.firmId), firmId: String(raw.firmId), savedByClient: raw.savedCount > 0, clickedByClient: raw.clickedCount > 0 };
                        return (
                          <FirmRow key={f.key} firm={f} status={statusByFirm[f.firmId]} cooldownDays={cooldownDays}
                            email={emailOf(f)} onEmail={(v) => setEmail(f.key, v)} onEmailBlur={() => saveEmail(f)}
                            onCompose={() => setComposer({ firm: f, cases: f.cases })}
                            extra={
                              <div className='flex flex-wrap items-center gap-1.5 mt-0.5'>
                                <span className='text-[12px] text-navy/[.6] mr-0.5'>Recommended on {f.caseCount} case{f.caseCount === 1 ? '' : 's'}:</span>
                                {f.cases.slice(0, 6).map((c) => (
                                  <button key={c._id} type='button' onClick={() => navigate('/admin/caseDetails/' + c._id)}
                                    title={c.savedByClient ? 'The client saved this firm on this case' : undefined}
                                    className={`font-plexmono text-[11px] leading-none rounded-[5px] py-[5px] px-1.5 cursor-pointer border hover:border-brass ${c.savedByClient ? 'text-brass bg-brass/[.08] border-brass/[.3]' : 'text-navy bg-navy/[.05] border-navy/[.12]'}`}>
                                    {c.caseNumber}
                                  </button>
                                ))}
                                {f.caseCount > 6 && <span className='text-[12px] text-navy/[.5]'>+{f.caseCount - 6} more</span>}
                              </div>} />
                        );
                      })}
                    </div>
                    <CasePager offset={firmOffset} limit={FIRMS_PAGE} total={firmsPage.total} shown={firmsPage.firms.length} onPage={setFirmOffset} />
                  </>}
          </>}

          {tab === 'cases' && <>
            <form onSubmit={runCaseSearch} className='flex flex-col sm:flex-row gap-2 sm:items-center'>
              {searchBox(caseQuery, setCaseQuery, 'Find a case number, e.g. CASE-1064')}
              <div className='flex gap-2'>
                <CaseBtn variant='secondary' onClick={runCaseSearch} className='!py-2.5 !px-4 h-10'>Search</CaseBtn>
                {searchHit && <CaseBtn variant='secondary' onClick={() => { setSearchHit(null); setCaseQuery(''); }} className='!py-2.5 !px-3 h-10'><X className='w-4 h-4' />Clear</CaseBtn>}
              </div>
            </form>
            {searchHit === 'none'
              ? <EmptyState title='No case found' body={`There's no case with the number "${caseQuery.trim()}".`} />
              : searchHit
                ? <CardStack>{caseCard(searchHit)}</CardStack>
                : cases === null
                  ? <CardSkeleton count={2} />
                  : cases.length === 0
                    ? <EmptyState title='No cases yet' body='Cases appear here once clients post them.' />
                    : <>
                        <CardStack>{cases.map(caseCard)}</CardStack>
                        <CasePager offset={caseOffset} limit={CASES_PAGE} total={casesTotal} shown={cases.length} onPage={setCaseOffset} />
                      </>}
          </>}
        </div>
      </div>

      <OutreachEmailModal
        open={!!composer}
        onClose={() => setComposer(null)}
        firm={composer?.firm}
        cases={composer?.cases}
        email={composer ? emailOf(composer.firm) : ''}
        onEmailChange={(v) => composer && setEmail(composer.firm.key, v)}
        canUseSaved={!!composer?.firm?.savedByClient}
        sendingReady={sendingReady}
        onSend={send}
      />
    </>
  );
}

export default FirmOutreach;
